import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react'

import { classnames, Icon, Search } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import { EVENT_SOURCE, FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { FilterTrackingProvider, useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import { trackFilterSelect as trackPanelFilterSelect } from 'Pods/SearchPage/Shared/Tracking/searchPageTracker'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store'
import {
  addKeywordTerms,
  applySearchWithin,
  cancelKeywordCommit,
  clearKeyword,
  commitKeyword,
  removeKeywordTerm,
} from 'Pods/SearchPage/Store/actions/keyword'
import { addTag, clearTags, removeTag } from 'Pods/SearchPage/Store/actions/tags'
import { mergeKeywordTerms } from 'Pods/SearchPage/Store/keywordTerms'

import Panel from './Components/Panel/Panel'
import useStagedResultCount from './useStagedResultCount'

const PANEL_ID = 'search-within-results-panel'
// Matches the full-screen panel in _search_within.scss; no named grid breakpoint sits at 480.
const FULL_SCREEN_QUERY = '(max-width: 479.98px)'
const PANEL_GAP = 4
const FOCUSABLE_SELECTOR = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

// Elements CSS hides (the below-lg actions while the full-screen footer shows) have no boxes.
const getFocusable = container => [...container.querySelectorAll(FOCUSABLE_SELECTOR)]
  .filter(element => element.getClientRects().length > 0)

// From lg the input sits in place; below it an icon opens the input inside the panel. Both render on
// the server and CSS picks one, so the bar never shifts on hydration.
//
// From lg every change searches as it happens. Below lg the panel edits a staged copy of the terms
// and tags, and only its Search button applies them (Figma 3268:99933).
function SearchWithinResults() {
  const translate = useTranslate()

  const keyword = useSearchPageStore(state => state.keyword)
  // Clears usually write the same '' the store already holds, so `keyword` alone would not rerender
  // this component and the input would keep showing a term that is no longer applied. The counter
  // changes on every clear (Store/slices/keyword.js), which is what makes the resync fire.
  const keywordClearCount = useSearchPageStore(state => state.keywordClearCount)
  const keywordTerms = useSearchPageStore(state => state.keywordTerms)
  const tags = useSearchPageStore(state => state.tags)
  const [value, setValue] = useState(keyword)
  const [isOpen, setIsOpen] = useState(false)
  // { terms, tags } while the panel was opened from the icon trigger (below lg); null from lg.
  const [staged, setStaged] = useState(null)
  // Below 480 the panel is fixed to the viewport, starting under the icon trigger, and is modal.
  const [panelTop, setPanelTop] = useState(0)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const trackFilterSelect = useTrackFilterSelect()
  // The text typed here, so the commit it produces is reported once and external writes are not.
  const typedKeywordRef = useRef(null)

  const rootRef = useRef(null)
  const panelRef = useRef(null)
  const triggerRef = useRef(null)
  const inlineInputRef = useRef(null)
  const panelInputRef = useRef(null)
  // Escape hands focus back to the inline input, whose focus handler would reopen the panel.
  const skipFocusOpenRef = useRef(false)

  useEffect(() => {
    if (keyword && keyword === typedKeywordRef.current) {
      typedKeywordRef.current = null
      trackFilterSelect(FILTER_NAME.WITHIN_KEYWORDS, keyword.trim())
    }
  }, [keyword, trackFilterSelect])

  // External store writes (Reset, Clear all filters, popstate restore) must win: sync the input and
  // drop any pending commit so stale text cannot resurrect the value the external write just cleared.
  useEffect(() => {
    cancelKeywordCommit()
    setValue(keyword)
  }, [keyword, keywordClearCount])

  useEffect(() => cancelKeywordCommit, [])

  // The second class hides the chat launcher, which would cover the footer's Apply button
  // (search_page/_chatbot.scss).
  useEffect(() => {
    if (!isOpen || !isFullScreen) return undefined

    document.body.classList.add('disable-page-scroll', 'search-within-full-screen')
    return () => document.body.classList.remove('disable-page-scroll', 'search-within-full-screen')
  }, [isOpen, isFullScreen])

  useEffect(() => {
    if (!isOpen) return undefined

    // offsetParent is null while CSS hides the panel's own input (from lg, where the inline input
    // already holds focus).
    if (panelInputRef.current?.offsetParent) panelInputRef.current.focus()

    const handleOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false)
    }
    // Focus leaving the panel closes it (handleOutside), so Tab would drop the staged terms and
    // land on the page behind the full-screen panel; wrap it inside instead.
    const handleTab = (event) => {
      const focusable = getFocusable(panelRef.current)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const { activeElement } = document
      if (event.shiftKey && (activeElement === first || !panelRef.current.contains(activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (activeElement === last || !panelRef.current.contains(activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Tab' && isFullScreen) {
        handleTab(event)
        return
      }
      if (event.key !== 'Escape') return
      // Closing unmounts the panel, which would drop keyboard focus to <body>; focus can also already
      // be there when the focused chip was just removed from the panel.
      const { activeElement } = document
      if (panelRef.current?.contains(activeElement) || activeElement === document.body) {
        if (triggerRef.current?.offsetParent) {
          triggerRef.current.focus()
        } else {
          skipFocusOpenRef.current = true
          inlineInputRef.current?.focus()
        }
      }
      setIsOpen(false)
    }

    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('focusin', handleOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('focusin', handleOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isFullScreen])

  // Only text that never reached the store is still unreported; a committed draft already was.
  const trackTypedText = (text) => {
    if (typedKeywordRef.current === null || text.trim() === '') return

    typedKeywordRef.current = null
    trackFilterSelect(FILTER_NAME.WITHIN_KEYWORDS, text.trim())
  }

  const handleAddTerms = (text) => {
    if (staged) {
      setStaged(current => ({ ...current, terms: mergeKeywordTerms(current.terms, text) }))
      setValue('')
      return
    }
    trackTypedText(text)
    addKeywordTerms(text)
  }

  const handleChange = (nextValue) => {
    if (nextValue.includes(',')) {
      handleAddTerms(nextValue)
      return
    }

    setValue(nextValue)
    typedKeywordRef.current = nextValue
    if (!staged) commitKeyword(nextValue)
  }

  const handleFieldKeyDown = (event) => {
    // isComposing: Enter that confirms an IME candidate (JP/ZH input) must not add a chip.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.target.tagName !== 'INPUT') return

    // Below lg nothing applies until Search, so Enter is the keyboard's Search button.
    if (staged) {
      event.preventDefault()
      handleSearch()
      return
    }
    if (value.trim() === '') return

    event.preventDefault()
    handleAddTerms(value)
  }

  const handleFieldFocus = useCallback(() => {
    if (skipFocusOpenRef.current) {
      skipFocusOpenRef.current = false
      return
    }
    setStaged(null)
    setIsFullScreen(false)
    setIsOpen(true)
  }, [])

  const handleFieldClick = useCallback(() => {
    setStaged(null)
    setIsFullScreen(false)
    setIsOpen(true)
  }, [])

  // Selecting or removing a term unmounts the button that held focus, so hand it to the visible
  // search input instead of letting it fall to <body>.
  const handleTermChange = useCallback(() => {
    const input = panelInputRef.current?.offsetParent ? panelInputRef.current : inlineInputRef.current
    input?.focus()
  }, [])
  // Each open starts a fresh copy, so closing without Search drops what was staged.
  const handleToggle = () => {
    if (!isOpen) {
      setStaged({ terms: keywordTerms, tags })
      setValue(keyword)
      setPanelTop(triggerRef.current.getBoundingClientRect().bottom + PANEL_GAP)
      setIsFullScreen(window.matchMedia(FULL_SCREEN_QUERY).matches)
    }
    setIsOpen(!isOpen)
  }

  const handleAddDraft = () => {
    handleAddTerms(value)
    handleTermChange()
  }

  // Staged terms and tags are reported only here, when they are applied: closing the panel without
  // Search drops them. The events match the ones from lg (one per add, tags under the tags source).
  const trackStagedSearch = (terms, stagedTags) => {
    typedKeywordRef.current = null
    const newTerms = terms.filter(term => !keywordTerms.includes(term))
    if (newTerms.length > 0) trackFilterSelect(FILTER_NAME.WITHIN_KEYWORDS, newTerms.join(' '))
    stagedTags
      .filter(tag => !tags.includes(tag))
      .forEach(tag => trackPanelFilterSelect({ name: FILTER_NAME.TAGS, value: tag, eventSource: EVENT_SOURCE.TAGS }))
  }

  // Closing unmounts the panel and the button that held focus, so hand focus back to the control
  // that opened it.
  const handleSearch = () => {
    if (staged) {
      const terms = mergeKeywordTerms(staged.terms, value)
      trackStagedSearch(terms, staged.tags)
      applySearchWithin({ terms, tags: staged.tags })
    } else {
      trackTypedText(value)
      if (value.trim() !== '') addKeywordTerms(value)
    }
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  const handleAddTag = (tag) => {
    if (!staged) {
      addTag(tag)
      return
    }
    setStaged(current => ({ ...current, tags: [...new Set([...current.tags, tag])] }))
  }

  const handleRemoveTag = (tag) => {
    if (!staged) {
      removeTag(tag)
      return
    }
    setStaged(current => ({ ...current, tags: current.tags.filter(currentTag => currentTag !== tag) }))
  }

  const handleRemoveTerm = (term) => {
    if (!staged) {
      removeKeywordTerm(term)
      return
    }
    setStaged(current => ({ ...current, terms: current.terms.filter(currentTerm => currentTerm !== term) }))
  }

  // Reset applies right away at every width; below lg it also empties the staged copy so the panel
  // matches the results. Reset then disables itself, so focus moves to the input.
  const handleReset = () => {
    clearKeyword()
    clearTags()
    if (staged) setStaged({ terms: [], tags: [] })
    handleTermChange()
  }

  const panelTerms = staged ? staged.terms : keywordTerms
  const panelTags = staged ? staged.tags : tags
  const panelDraft = staged ? value.trim() : keyword
  const isResetDisabled = panelDraft === '' && panelTerms.length === 0 && panelTags.length === 0
  // The staged copy outlives the panel until the next open, so only count while it is open.
  const resultCount = useStagedResultCount(isOpen ? staged : null, value)

  const label = translate('search_page.filters.search_within.button_label')
  const placeholder = translate('search_page.filters.secondary.search_placeholder')

  return (
    <div ref={rootRef} className="search-within">
      <div
        className="search-within__field"
        onFocus={handleFieldFocus}
        onClick={handleFieldClick}
        onKeyDown={handleFieldKeyDown}
        role="presentation"
      >
        <FilterSearchInput
          id="search-within-results"
          className="search-within__input"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          ariaLabel={label}
          inputRef={inlineInputRef}
        />
      </div>
      <button
        ref={triggerRef}
        type="button"
        className={classnames(
          'cgt-text-input cgt-text-input--medium cgt-text-input--primary filter-wrapper__trigger',
          'search-within__trigger',
          (keyword !== '' || keywordTerms.length > 0 || tags.length > 0) && 'filter-wrapper__trigger--filled',
        )}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? PANEL_ID : undefined}
        onClick={handleToggle}
      >
        <Icon notSpaced svgComponent={Search} size="small" color="darker" className="search-within__trigger-icon" />
      </button>
      {isOpen && (
        <div
          ref={panelRef}
          id={PANEL_ID}
          className="search-within__panel"
          style={{ '--search-within-panel-top': `${panelTop}px` }}
          role="dialog"
          aria-modal={isFullScreen || undefined}
          aria-label={label}
        >
          <div className="search-within__panel-input" onKeyDown={handleFieldKeyDown} role="presentation">
            <FilterSearchInput
              id="search-within-results-compact"
              className="search-within__input"
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              ariaLabel={label}
              inputRef={panelInputRef}
            />
          </div>
          <FilterTrackingProvider eventSource={EVENT_SOURCE.TAGS}>
            <Panel
              draft={value}
              isStaged={Boolean(staged)}
              resultCount={resultCount}
              terms={panelTerms}
              tags={panelTags}
              isResetDisabled={isResetDisabled}
              onAddDraft={handleAddDraft}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              onRemoveTerm={handleRemoveTerm}
              onReset={handleReset}
              onSearch={handleSearch}
              onTermChange={handleTermChange}
            />
          </FilterTrackingProvider>
        </div>
      )}
    </div>
  )
}

export default memo(SearchWithinResults)
